import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { ChartData } from '../types';
import { getWebSocketUrl } from '../utils/api';

interface UseSocketReturn {
  socket: Socket | null;
  isConnected: boolean;
  subscribe: (symbol: string) => void;
  unsubscribe: (symbol: string) => void;
  onChartData: (callback: (data: ChartData[]) => void) => void;
  onError: (callback: (error: string) => void) => void;
}

export const useSocket = (): UseSocketReturn => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    // Initialize socket connection
    const socketUrl = getWebSocketUrl();
    console.log('Connecting to WebSocket:', socketUrl);
    
    const newSocket = io(socketUrl, {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
      timeout: 20000,
      forceNew: true
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    // Connection event handlers
    newSocket.on('connect', () => {
      console.log('Connected to server');
      setIsConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from server');
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Connection error:', error);
      setIsConnected(false);
    });

    // Cleanup on unmount
    return () => {
      newSocket.close();
      socketRef.current = null;
    };
  }, []);

  const subscribe = (symbol: string) => {
    if (socketRef.current) {
      socketRef.current.emit('subscribe', symbol);
      console.log(`Subscribed to ${symbol}`);
    }
  };

  const unsubscribe = (symbol: string) => {
    if (socketRef.current) {
      socketRef.current.emit('unsubscribe', symbol);
      console.log(`Unsubscribed from ${symbol}`);
    }
  };


  const onChartData = (callback: (data: ChartData[]) => void) => {
    if (socketRef.current) {
      socketRef.current.off('chart-data'); // Remove previous listener
      socketRef.current.on('chart-data', (data) => {
        console.log('Socket received chart-data:', data);
        console.log('Data type:', typeof data);
        console.log('Data length:', Array.isArray(data) ? data.length : 'not array');
        console.log('Socket connected:', socketRef.current?.connected);
        console.log('Data sample:', Array.isArray(data) && data.length > 0 ? data[0] : 'no data');
        if (Array.isArray(data) && data.length > 0) {
          console.log('Data OHLC values:', data.map(d => ({
            time: d.time,
            open: d.open,
            high: d.high,
            low: d.low,
            close: d.close
          })));
        }
        callback(data);
      });
    } else {
      console.log('Socket not available for chart-data listener');
    }
  };

  const onError = (callback: (error: string) => void) => {
    if (socketRef.current) {
      socketRef.current.off('error'); // Remove previous listener
      socketRef.current.on('error', callback);
    }
  };

  return {
    socket,
    isConnected,
    subscribe,
    unsubscribe,
    onChartData,
    onError
  };
};
